import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, apiFetch } from "@/lib/api";
import { toast } from "@/lib/toast";
import type { Enrichment, Job, Repo } from "@/types";

export type EnrichKind = "summary" | "ideas";

export interface EnrichState {
  fullName: string;
  kind: EnrichKind;
  status: "starting" | Job["status"];
  enrichment?: Enrichment;
}

const POLL_MS = 1500;
const ENDPOINT: Record<EnrichKind, string> = { summary: "summary", ideas: "enrich" };

export function useEnrich() {
  const [state, setState] = useState<EnrichState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic run id — any start()/close()/unmount bumps it so stale polls and
  // in-flight POSTs from a previous action bail instead of overlapping.
  const runId = useRef(0);

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(
    () => () => {
      runId.current += 1; // cancel any active poll on unmount
      clear();
    },
    [clear],
  );

  const close = useCallback(() => {
    runId.current += 1;
    clear();
    setState(null);
  }, [clear]);

  const start = useCallback(
    async (repo: Repo, kind: EnrichKind) => {
      runId.current += 1;
      const myRun = runId.current;
      clear();
      const [owner, name] = repo.full_name.split("/");
      setState({ fullName: repo.full_name, kind, status: "starting" });

      const poll = async (jobId: string) => {
        if (runId.current !== myRun) return;
        try {
          const job = await apiFetch<Job>(`/api/jobs/${jobId}`);
          if (runId.current !== myRun) return;
          if (job.status === "done") {
            setState({ fullName: repo.full_name, kind, status: "done", enrichment: job.enrichment });
            return;
          }
          if (job.status === "failed") {
            setState({ fullName: repo.full_name, kind, status: "failed" });
            toast.error("Не удалось сгенерировать — задан ли ключ Anthropic?");
            return;
          }
          setState({ fullName: repo.full_name, kind, status: job.status });
          timer.current = setTimeout(() => poll(jobId), POLL_MS);
        } catch (e) {
          if (runId.current === myRun && !(e instanceof ApiError && e.status === 401)) setState(null);
        }
      };

      try {
        const { job_id } = await apiFetch<{ job_id: string }>(
          `/api/repos/${owner}/${name}/${ENDPOINT[kind]}`,
          { json: {} },
        );
        if (runId.current !== myRun) return;
        poll(job_id);
      } catch (e) {
        if (runId.current === myRun && !(e instanceof ApiError && e.status === 401)) {
          toast.error("Не удалось запустить генерацию");
          setState(null);
        }
      }
    },
    [clear],
  );

  return { state, start, close };
}
